import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import ListItem from "@insite/mobius/Lists";
import { ProductCarouselIdState } from "@tsesco/Store/CheckoutReviewAndSubmitCarrierService/CheckoutReviewAndSubmitReducer";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { connect } from "react-redux";

const mapStateToProps = (state: ProductCarouselIdState, ownProps: WidgetProps) => {
   return {
        relatedProducts: state?.ProductCarouselIdReducer?.widgetId === "" ? []:getProductsForProductInfoList(state, state?.ProductCarouselIdReducer?.widgetId),
 }
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;


export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    keyFeatureTitle?: InjectableCss;
    seeFullFeature?: InjectableCss;
    attributeTypeWrapper?: InjectableCss;
    specificationHeadingWrapper?: InjectableCss;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    // attributeTypeLabelText: {
    //     fontFamily: "Roboto-Bold",
    //     size: 16,
    //     css: css`
    //         color: ${getColor("text.accent")};
    //     `,
    // },
     attributeTypeLabelText: {
            // weight: "bold",
            css: css`
                padding: 10px;
                border-right: 1px solid #e1dde2;
                font-size: 16px;
                font-family: "Roboto-Medium";
                line-height: 24px;
                word-break: break-word;
                color: ${getColor("text.accent")};
                border-top: 1px solid #e1dde2;
            `,
        },
    attributeTypeValuesText: {
            css: css`
                padding: 10px;
                font-size: 16px;
                font-weight: 400;
                line-height: 24px;
                color: ${getColor("text.accent")};
                border-top: 1px solid #e1dde2;
            `,
        },
    keyFeatureTitle: {
        css: css`
            margin: 0 0 10px 0;
            color: #000000;
            font-family: "Roboto-Bold";
            font-size: 22px;
        `,
    },
    seeFullFeature: {
        css: css`
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
            margin-top: 8px;
            font-family: "Roboto-Medium";
            width: auto;
            display: flex;
            ${TypographyStyle} {
                color: ${getColor("primary")};
                font-family: "Roboto-Medium";
            }
            &:hover {
                text-decoration: underline;
                color: ${getColor("primary")};
            }
        `,
    },
     attributeTypeWrapper: {
            css: css`
                display: grid;
                grid-template-columns: 30% 70%;
                border-right: 1px solid #e1dde2;
                border-left: 1px solid #e1dde2;
                border-bottom: 1px solid #e1dde2;
                &:first-child {
                    border-top: 1px solid #e1dde2;
                }
                /* margin-top: 20px; */
                &:nth-child(odd) {
                    background-color: #f1f1f1;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            grid-template-columns: 40% 60%;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
      specificationHeadingWrapper: {
            css: css`
                font-size: 28px;
                color: ${getColor("primary")};
                font-family: "Roboto-Bold";
                background-color: ${getColor("common.border")};
                padding: 0.5rem;
                border-radius: 4px;
                display: block;
                margin-bottom: 1rem;
            `,
        },
};

const goToSpecification = () => {
    const specificationSection = document.getElementById("specification");
    if (specificationSection) {
        const offset = 150;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = specificationSection.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
        });
    }
};
const goToDescription = () => {
    const descriptionSection = document.getElementById("description");
    if (descriptionSection) {
        const offset = 150;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = descriptionSection.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
        });
    }
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({
    product: { brand, attributeTypes, specifications, detail, content, manufacturerItem, productNumber },
    id,
    relatedProducts
}) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const limitedAttributeTypes = (attributeTypes?.filter(attributeType => attributeType.includeOnProduct) ?? []).slice(
        0,
        5,
    );

    const showSpecificationLink = limitedAttributeTypes.length > 0 || brand?.name || detail?.unspsc || detail?.upcCode;

    const descriptionLink = specifications?.length !== 0 || content?.htmlContent !== "";

    const hasValidHtmlContent = content?.htmlContent && !/<body>(&nbsp;|\s+)<\/body>/i.test(content.htmlContent);

    const showDescriptionLink = descriptionLink && hasValidHtmlContent;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            <Typography as="h2" {...styles.keyFeatureTitle}>
            {brand && brand.name} {manufacturerItem && manufacturerItem} {translate("Key Features")}
            </Typography>
            <Zone contentId={id} zoneName="Content7" />
            <StyledWrapper>
                {brand && (
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                    </StyledWrapper>
                )}
                {manufacturerItem && (
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Part Number # ")}</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{manufacturerItem}</Typography>
                    </StyledWrapper>
                )}
                {productNumber && (
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("SKU #")}</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{productNumber}</Typography>
                    </StyledWrapper>
                )}
                
                {attributeTypes &&
                    attributeTypes.map(attributeType => (
                        <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                            <Typography {...styles.attributeTypeLabelText}>
                                {attributeType.label || attributeType.name}:&nbsp;
                            </Typography>
                            <Typography {...styles.attributeTypeValuesText}>
                                {attributeType
                                    .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </Typography>
                        </StyledWrapper>
                    ))}
                {detail?.unspsc && (
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("UNSPSC")}:&nbsp;</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{detail?.unspsc}</Typography>
                    </StyledWrapper>
                )}
                {detail?.upcCode && (
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("UPC")}:&nbsp;</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{detail?.upcCode}</Typography>
                    </StyledWrapper>
                )}
                {relatedProducts?.length ?  <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Cross Sell Products")}:&nbsp;</Typography>
                        <Typography {...styles.attributeTypeValuesText}>
                            
                            {relatedProducts?.map((item,index)=>{
                            return <ListItem
                                    key={index}
                                    css={css`
                                        list-style: disc ;
                                        padding-top: 0.25rem;
                                    `}
                                >
                                    {item?.productTitle}
                                </ListItem>
                            })}
                            
                        </Typography>
                    </StyledWrapper>: null
                }
                
            </StyledWrapper>

{/* 
            {showSpecificationLink && (
                <Link {...styles.seeFullFeature} onClick={goToSpecification}>
                    {siteMessage("see_product_specification")}
                </Link>
            )} */}
            {/* {showDescriptionLink && (
                <Link {...styles.seeFullFeature} onClick={goToDescription}>
                    {siteMessage("see_product_description")}
                </Link>
            )} */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsAttributes)),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
